/**
 * Endpoint Tester Utility
 * This file provides functions to test the availability and response of API endpoints
 */
import axios from 'axios';

// Base URLs
const BASE_URL = '/cxf/uns';
const BILLING_API = `${BASE_URL}/billing`;
const PAYMENT_API = `${BASE_URL}/payment`;

/**
 * Test all API endpoints for a specific site and unit
 * @param {string} siteName - The site name to test with
 * @param {number} unitId - The unit ID to test with
 * @returns {Promise<object>} Results of all tests
 */
export const testAllEndpoints = async (siteName, unitId) => {
  if (!siteName || !unitId) {
    // console.error('Site name and unit ID are required');
    return {
      success: false,
      error: 'Site name and unit ID are required'
    };
  }

  const results = {
    timestamp: new Date().toISOString(),
    site: siteName,
    unit: unitId,
    endpoints: []
  };

  try {
    // Test the only functioning endpoint: Account by Unit
    await testEndpoint(
      `${BILLING_API}/accountsByUnit?site=${siteName}&unit=${unitId}`,
      'Account by Unit',
      results
    );

    // Test payment by account endpoint
    await testEndpoint(
      `${PAYMENT_API}/payments/byAccount/B42690FB-0171-4BBC-896B-C1CA394EBD57`,
      'Payments by Account',
      results
    );

    // console.log('Endpoint test results:', results);
    return results;
  } catch (error) {
    // console.error('Error testing endpoints:', error);
    results.success = false;
    results.error = error.message;
    return results;
  }
};

/**
 * Test a single endpoint and record the result
 * @param {string} url - The endpoint URL to test
 * @param {string} name - Name of the endpoint for reporting
 * @param {object} results - Results object to update
 */
const testEndpoint = async (url, name, results) => {
  try {
    // console.log(`Testing endpoint: ${name} (${url})`);
    const startTime = Date.now();
    const response = await axios.get(url, { timeout: 5000 });
    const endTime = Date.now();
    
    const result = {
      name,
      url,
      status: response.status,
      statusText: response.statusText,
      responseTime: endTime - startTime,
      success: true,
      data: response.data
    };
    
    // console.log(`✓ Success: ${name} (${response.status})`);
    results.endpoints.push(result);
  } catch (error) {
    const result = {
      name,
      url,
      success: false,
      error: error.message
    };
    
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      result.status = error.response.status;
      result.statusText = error.response.statusText;
      result.data = error.response.data;
    }
    
    // console.error(`✗ Failed: ${name} - ${error.message}`);
    results.endpoints.push(result);
  }
};

/**
 * Run the endpoint tests and display results in the console
 * This function is intended to be called manually when needed
 * @param {string} siteName - The site name to test with 
 * @param {number} unitId - The unit ID to test with
 * @returns {Promise<object>} Results of all tests
 */
export const runEndpointTests = async (siteName, unitId) => {
  // console.log('=== Starting API Endpoint Tests ===');
  // console.log(`Testing with site: ${siteName}, unit: ${unitId}`);
  
  try {
    const results = await testAllEndpoints(siteName, unitId);
    
    // console.log('=== Endpoint Test Results ===');
    
    // Count successes
    const successful = results.endpoints.filter(endpoint => endpoint.success).length;
    const total = results.endpoints.length;
    
    // console.log(`${successful}/${total} endpoints working`);
    
    // Success summary
    // console.log('\n=== Working Endpoints ===');
    results.endpoints
      .filter(endpoint => endpoint.success)
      .forEach(endpoint => {
        // console.log(`✓ ${endpoint.name}: ${endpoint.url}`);
      });
    
    // Failure summary
    // console.log('\n=== Failed Endpoints ===');
    results.endpoints
      .filter(endpoint => !endpoint.success)
      .forEach(endpoint => {
        // console.log(`✗ ${endpoint.name}: ${endpoint.url}`);
        // console.log(`  Error: ${endpoint.error}`);
      });
    
    return results;
  } catch (error) {
    // console.error('Error during endpoint tests:', error);
    throw error;
  }
};

// Example usage:
// runEndpointTests('Lab', 1); 